import { useCallback, useMemo } from 'react';
import MomentLocaleUtils from 'react-day-picker/moment';
import mapValues from 'lodash-es/mapValues';

import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { useApiCountryConfigurationNavigationRetrieve } from '@rover/rsdk/src/apiClient/latest/api-country-configuration-navigation-retrieve';
import type { Fact } from '@rover/rsdk/src/apiClient/latest/model/fact';
import type { SeoLocationDetailsFacts } from '@rover/rsdk/src/apiClient/latest/model/seoLocationDetailsFacts';
import { formatCurrency } from '@rover/utilities/currency';

type FormattedFact = {
  raw: number;
  asPercent: string;
  asNumber: string;
  asCurrency: string;
  asWeekCurrency: string;
  yearMonthLastUpdated: string;
};

// Using excessively-generic key types in a `Record` can allow
// for type safety errors. For example, `Record<string, Something>`
// implies that every possible string is a valid property of that object
// and will return an instance of `Something`. Work around that by wrapping
// `Record` in a `Partial` to make all properties (correctly) optional.
type SafeRecord<K extends string | number | symbol, V> = Partial<Record<K, V>>;

export default function useFacts(
  facts: SeoLocationDetailsFacts
): SafeRecord<string, FormattedFact> {
  const { DF, locale } = useI18nContext();

  const date = useCallback(
    (unparsedDate: string): string =>
      MomentLocaleUtils.formatDate(new Date(unparsedDate), DF.MONTH_YEAR_MED, locale),
    [DF.MONTH_YEAR_MED, locale]
  );

  const {
    isLoading: isLoadingCountryConfig,
    isError: isErrorCountryConfig,
    data: countryConfig,
  } = useApiCountryConfigurationNavigationRetrieve();

  const currencyCodeNotFound = isLoadingCountryConfig || isErrorCountryConfig;

  const currencyCode = currencyCodeNotFound ? 'USD' : countryConfig!.currencyCode;

  const currency = useCallback(
    (value: number): string =>
      formatCurrency(value, currencyCode, locale, { includeDecimal: false }),
    [locale, currencyCode]
  );

  const getFormattedFact = useCallback(
    ({ value, modified }: Fact): FormattedFact => ({
      get raw() {
        return value;
      },
      get asPercent() {
        // toLocaleString expects the value to be between 0 and 1.
        // If the given value is > 1, we can assume it's already calculated as a percent,
        // so we need to divide it by 100
        const val = value > 1 ? value / 100 : value;
        return val.toLocaleString(locale, { style: 'percent' });
      },
      get asNumber() {
        return value.toLocaleString(locale);
      },
      get asCurrency() {
        return currency(value);
      },
      get asWeekCurrency() {
        return currency(value * 7);
      },
      get yearMonthLastUpdated() {
        return date(modified);
      },
    }),
    [currency, date, locale]
  );

  return useMemo(() => {
    if (currencyCodeNotFound) {
      return {};
    }
    return mapValues(facts, getFormattedFact);
  }, [facts, getFormattedFact, currencyCodeNotFound]);
}
